import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import {
  useGetGroupsQuery,
  useGetCompaniesQuery,
  type Group,
  type Company,
  TradeType,
} from '../generated/graphql'
import { useAuth } from '../AuthProvider'
import { UserRole } from '@valiant/ui-shared'
import { useQueryURLParams } from '@valiant/ui-shared'

interface NavigationContextType {
  selectedGroupId: string
  setSelectedGroupId: (groupId: string) => void
  selectedCompanyId: string
  setSelectedCompanyId: (companyId: string) => void
  isSwapMode: boolean
  setIsSwapMode: (isSwapMode: boolean) => void
  groupsData: Group[] | undefined
  groupsLoading: boolean
  selectedGroup: Group | undefined
  companiesData: Company[] | undefined
  companiesLoading: boolean
  selectedCompany: Company | undefined
  notificationsOpen: boolean
  setNotificationsOpen: (notificationsOpen: boolean) => void
  depositFundsOpen: boolean
  setDepositFundsOpen: (depositFundsOpen: boolean) => void
  swapOpen: boolean
  setSwapOpen: (swapOpen: boolean) => void
  profileOpen: boolean
  setProfileOpen: (profileOpen: boolean) => void
  addWhitelistedAddressOpen: boolean
  setAddWhitelistedAddressOpen: (addWhitelistedAddressOpen: boolean) => void
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined)

interface NavigationProviderProps {
  children: ReactNode
}

export function NavigationProvider({ children }: NavigationProviderProps) {
  const { auth } = useAuth()
  const userRole = auth?.role

  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [depositFundsOpen, setDepositFundsOpen] = useState(false)
  const [swapOpen, setSwapOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [addWhitelistedAddressOpen, setAddWhitelistedAddressOpen] = useState(false)

  const [selectedGroupId, setSelectedGroupId] = useState<string>(() => {
    return localStorage.getItem('selectedGroupId') || ''
  })
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(() => {
    return localStorage.getItem('selectedCompanyId') || ''
  })
  const [isSwapMode, setIsSwapMode] = useState<boolean>(true)
  const [dashboardMode] = useQueryURLParams('dashboardMode')

  // Set initial swap mode based on user role, URL param and stored preference
  useEffect(() => {
    // Priority order: userRole > URL param > localStorage
    if (userRole === UserRole.Trader) {
      setIsSwapMode(true)
      localStorage.setItem('dashboardMode', 'swap')
      return
    }

    if (dashboardMode === 'swap') {
      setIsSwapMode(true)
      localStorage.setItem('dashboardMode', 'swap')
      return
    }

    if (dashboardMode === 'admin') {
      setIsSwapMode(false)
      localStorage.setItem('dashboardMode', 'admin')
      return
    }

    const storedDashboardMode = localStorage.getItem('dashboardMode')

    if (!storedDashboardMode) {
      // No stored preference – default to swap mode
      setIsSwapMode(true)
      localStorage.setItem('dashboardMode', 'swap')
      return
    }

    const shouldBeSwapMode = storedDashboardMode === 'swap'
    setIsSwapMode(shouldBeSwapMode)
  }, [userRole, dashboardMode])

  // Fetch groups data
  const [{ data: groupsQueryData, fetching: groupsLoading }] = useGetGroupsQuery({
    variables: {},
    pause: !auth,
  })
  const groupsData = groupsQueryData?.getGroups

  // Fetch companies data
  const [{ data: companiesQueryData, fetching: companiesLoading }] = useGetCompaniesQuery({
    pause: !auth,
  })
  const companiesData = companiesQueryData?.getCompanies.filter(
    (c) => c.tradeType !== TradeType.Deactivated,
  )

  // Find selected group
  const selectedGroup = groupsData?.find((g) => g.groupId === selectedGroupId)

  // Find selected company
  const selectedCompany = companiesData?.find((c) => c.id === selectedCompanyId)

  // Set default group selection when groups load (only if no localStorage value)
  if (groupsData?.length && !selectedGroupId && !localStorage.getItem('selectedGroupId')) {
    setSelectedGroupId(groupsData[0].groupId)
  }

  // Set default company selection when switching to swap mode or when companies load (only if no localStorage value)
  if (
    companiesData?.length &&
    !selectedCompanyId &&
    isSwapMode &&
    !localStorage.getItem('selectedCompanyId')
  ) {
    setSelectedCompanyId(companiesData[0].id)
  }

  // When switching to swap mode, auto-select first company if none selected
  // Only allow mode changes for non-USER roles
  const handleModeChange = (swapMode: boolean) => {
    if (userRole === UserRole.Trader) {
      return // USER role should always stay in swap mode
    }
    setIsSwapMode(swapMode)
    localStorage.setItem('dashboardMode', swapMode ? 'swap' : 'admin')
    if (
      swapMode &&
      companiesData?.length &&
      !selectedCompanyId &&
      !localStorage.getItem('selectedCompanyId')
    ) {
      setSelectedCompanyId(companiesData[0].id)
    }
  }

  // Wrapper functions that save to localStorage
  const handleSetSelectedGroupId = (groupId: string) => {
    setSelectedGroupId(groupId)
    localStorage.setItem('selectedGroupId', groupId)
  }

  const handleSetSelectedCompanyId = (companyId: string) => {
    setSelectedCompanyId(companyId)
    localStorage.setItem('selectedCompanyId', companyId)
  }

  return (
    <NavigationContext.Provider
      value={{
        selectedGroupId,
        setSelectedGroupId: handleSetSelectedGroupId,
        selectedCompanyId,
        setSelectedCompanyId: handleSetSelectedCompanyId,
        isSwapMode,
        setIsSwapMode: handleModeChange,
        groupsData,
        groupsLoading,
        selectedGroup,
        companiesData,
        companiesLoading,
        selectedCompany,
        notificationsOpen,
        setNotificationsOpen,
        depositFundsOpen,
        setDepositFundsOpen,
        swapOpen,
        setSwapOpen,
        profileOpen,
        setProfileOpen,
        addWhitelistedAddressOpen,
        setAddWhitelistedAddressOpen,
      }}
    >
      {children}
    </NavigationContext.Provider>
  )
}

export function useNavigation() {
  const context = useContext(NavigationContext)
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }
  return context
}
